import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { View } from 'react-native'
import { Button } from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import { MelddTextInput } from '../../../../../@ui/form/meldd-input'
import { useModalDialog } from '../../../../../@ui/containers/modals/modal-dialogue.provider'
import i18n from '../../../../../i18n'
import { createStyle } from '../../../../../contexts/global-styles.provider'
import Row from '../../../../../@ui/containers/grid/row'
import { useIsMobileDevice } from '../../../../../hooks/is-mobile.hook'
import { MelddTextArea } from '../../../../../@ui'

const PRESET_TABS = (t: typeof i18n.t) => [
  {
    label: t('make-it-longer-label'),
    value: 'make_longer',
    prompt: t('make-it-longer-prompt'),
  },
  {
    label: t('make-it-shorter-label'),
    value: 'make_shorter',
    prompt: t('make-it-shorter-prompt'),
  },
  {
    label: t('make-it-more-critical-label'),
    value: 'make_critical',
    prompt: t('make-it-more-critical-prompt'),
  },
  {
    label: t('provide-balance-opinion-label'),
    value: 'balance_opinion',
    prompt: t('provide-balance-opinion-prompt'),
  },
]

const LLMModalContent = forwardRef(
  (
    {
      initialValues,
    }: { initialValues: { userPrompt: string; systemPrompt: string } },
    ref
  ) => {
    const { t } = useTranslation(['llm-modal'])
    const styles = useStyle()
    const [userPrompt, setUserPrompt] = useState(initialValues.userPrompt)
    const [systemPrompt, setSystemPrompt] = useState(initialValues.systemPrompt)
    const [error, setError] = useState<string | null>(null)
    const tabs = useMemo(() => PRESET_TABS(t), [t])
    const isMobile = useIsMobileDevice()
    const validate = useCallback(() => {
      if (!userPrompt.trim() && !systemPrompt.trim()) {
        setError(t('At least one prompt is required.'))
        return false
      }
      setError(null)
      return true
    }, [userPrompt, systemPrompt, t])

    useImperativeHandle(
      ref,
      () => ({
        submit: async (
          callback: (params: {
            userPrompt: string
            systemPrompt: string
          }) => void
        ) => {
          if (!validate()) return null
          callback({ userPrompt, systemPrompt })
          return true
        },
      }),
      [userPrompt, systemPrompt, validate]
    )

    const handleTabClick = (tabValue: string) => {
      const tab = tabs.find((t) => t.value === tabValue)
      if (tab) {
        setUserPrompt(userPrompt + `\n` + tab.prompt)
      }
    }

    return (
      <View style={styles.container}>
        {!isMobile && (
          <Row style={{ alignItems: 'center' }} wrap>
            {tabs.map((tab) => (
              <View key={tab.value} style={{ marginRight: 8 }}>
                <Button
                  onPress={() => handleTabClick(tab.value)}
                  compact
                  style={{
                    minWidth: 40,
                    borderRadius: 20,
                    marginBottom: 0,
                  }}
                >
                  {tab.label}
                </Button>
              </View>
            ))}
          </Row>
        )}
        <MelddTextArea
          placeholder={t('prompt-placeholder')}
          value={userPrompt}
          minHeight={100}
          onChangeText={setUserPrompt}
          label={t('User Prompt')}
        />
        <MelddTextArea
          minHeight={100}
          placeholder={t('system-prompt-placeholder')}
          value={systemPrompt}
          onChangeText={setSystemPrompt}
          label={t('System Prompt')}
        />
        {error ? (
          <View style={{ marginTop: 8 }}>
            <MelddTextInput
              label={t('Error')}
              form={undefined}
              name="error"
              value={error}
              readOnly={true}
              style={{ color: 'red' }}
            />
          </View>
        ) : null}
      </View>
    )
  }
)

export type LLMModalRef = {
  open: ({
    model,
    systemPrompt,
    userPrompt,
  }: {
    model: string
    systemPrompt: string
    userPrompt: string
  }) => void
  close: () => void
}

export const LLMModal = forwardRef<
  LLMModalRef,
  { onGenerate: (userPrompt: string, systemPrompt: string) => void }
>(({ onGenerate }, ref) => {
  const { setDialog } = useModalDialog()
  const { t } = useTranslation(['llm-modal'])
  const formRef = useRef<{
    submit: (
      callback: (params: { userPrompt: string; systemPrompt: string }) => void
    ) => void
  }>(null)

  useImperativeHandle(ref, () => {
    return {
      open: ({
        userPrompt,
        systemPrompt,
        model,
      }: {
        userPrompt: string
        systemPrompt: string
        model: string
      }) => {
        setDialog({
          title: t('llm-modal-title') + ': ' + model,
          body: (
            <LLMModalContent
              ref={formRef}
              initialValues={{ userPrompt, systemPrompt }}
            />
          ),
          primaryLabel: t('create'),
          primaryAction: async () => {
            formRef.current?.submit(async (params) => {
              onGenerate(params.userPrompt, params.systemPrompt)
            })
            setDialog(null)
          },
          secondaryLabel: t('cancel'),
          closeAction: () => {
            setDialog(null)
          },
          secondaryAction: async () => {
            setDialog(null)
          },
        })
      },
      close: () => setDialog(null),
    }
  })

  return null
})

const useStyle = createStyle(({}) => ({
  container: {
    width: '100%',
    gap: 8,
  },
}))
